import { WbTextField } from '@agilelab/plugin-wb-platform';
import { useCallback, useMemo } from 'react';
import { useFormExtraConfigContext } from '../../../contexts/FormExtraConfigContext';
import { extractCustomProperties, isHidden } from '../../utils';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';
import { RegexPickerProps } from './schema';

export const RegexPicker = (props: RegexPickerProps) => {
  const {
    onChange,
    schema: {
      title = 'RegExPicker',
      description = 'Collect and validate data with a regular expression',
    },
    required,
    rawErrors,
    formData,
    idSchema,
    placeholder,
    uiSchema,
    formContext,
  } = props;
  const userSpecifiedPlaceholder = uiSchema['ui:placeholder'] ?? placeholder;
  const customProps = extractCustomProperties(uiSchema);

  const { debounceMs } = useFormExtraConfigContext();

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [idSchema, rootSchema]);

  // Handle changes with dependency manager support
  const handleChange = useCallback(
    (value: string | undefined) => {
      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(value);
      }
    },
    [onChange, dependencyManager, fieldPath],
  );

  return (
    <WbTextField
      id={idSchema?.$id}
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      label={title}
      placeholder={userSpecifiedPlaceholder}
      helperText={description}
      required={required}
      value={formData ?? ''}
      debounceMs={debounceMs}
      onChange={({ target: { value } }) => handleChange(value || undefined)}
      error={rawErrors?.length > 0}
      {...customProps}
    />
  );
};
